import React, { useState, useEffect, useContext } from 'react';
import './WaveExportV4.css';
import GameAssetsContext from '../../../../XXX/context/GameAssetContext';
import { WaveData, WaveSelectionManager } from '../WaveTable';
import { ChampionBuildData } from '../ItemBuild';

// Simplified interface - only what we actually need
interface CustomGroup {
  id: string;
  name: string;
  selectedWaves: number[];
  isExpanded: boolean;
  isLoaded: boolean; // Track if exported to analysis grid
}

interface Props {
  // Wave data from backend
  primaryWaves: WaveData[];
  opponentWaves?: WaveData[];
  
  // Champion info
  primaryChampionName: string;
  opponentChampionName?: string;
  
  // Champion build data for display purposes only
  primaryChampionData?: ChampionBuildData | null;
  opponentChampionData?: ChampionBuildData | null;
  
  // Selection manager from wave table
  selectionManager?: WaveSelectionManager;
  
  // Currently loaded waves in the analysis grid (for tracking what's already exported)
  loadedWaves?: number[];
  
  // Item display
  getItemImageUrl: (itemId: number) => string;
  
  // Export callback
  onWaveExport?: (selectedWaves: number[], groupLabel?: string) => Promise<void>;
  
  // Styling
  className?: string;
  error?: string;
}

const WaveExportV4: React.FC<Props> = ({
  primaryWaves = [],
  opponentWaves = [],
  primaryChampionName,
  opponentChampionName,
  primaryChampionData,
  opponentChampionData,
  selectionManager,
  loadedWaves = [],
  getItemImageUrl,
  onWaveExport,
  className = '',
  error = null
}) => {
  const { championsAssets } = useContext(GameAssetsContext);
  
  // Simple state management
  const [groups, setGroups] = useState<CustomGroup[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [groupCounter, setGroupCounter] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  
  console.log('[WaveExportV4] 🆕 Simplified component initialized');
  
  // Update active group when selections change
  useEffect(() => {
    if (!selectionManager || !activeGroupId) return;
    
    const selectedWaves = selectionManager.getSelectedWaves();
    const activeGroup = groups.find(g => g.id === activeGroupId);
    
    // Don't update exported/locked groups
    if (activeGroup?.isLoaded) {
      console.log('[WaveExportV4] 🔒 Ignoring selection changes for locked group:', activeGroup.name);
      return;
    }
    
    setGroups(prev => prev.map(group => 
      group.id === activeGroupId 
        ? { ...group, selectedWaves: [...selectedWaves] }
        : group
    ));
  }, [selectionManager?.getSelectedWaves(), activeGroupId, groups]);
  
  // Auto-create first group when waves are selected and no groups exist
  useEffect(() => {
    if (!selectionManager) return;
    
    const selectedWaves = selectionManager.getSelectedWaves();
    
    if (selectedWaves.length > 0 && groups.length === 0) {
      console.log('[WaveExportV4] 🔥 Auto-creating first group with initial waves:', selectedWaves);
      createFirstGroupWithWaves(selectedWaves);
    }
  }, [selectionManager?.getSelectedWaves(), groups.length]);
  
  // Create first group with initial wave selections (don't clear table)
  const createFirstGroupWithWaves = (initialWaves: number[]) => {
    if (!selectionManager) return;
    
    const newGroupId = `group_${Date.now()}`;
    
    // Create new group with the initial waves already selected
    const newGroup: CustomGroup = {
      id: newGroupId,
      name: `Custom Group ${groupCounter}`,
      selectedWaves: [...initialWaves], // Use the initial waves
      isExpanded: true,
      isLoaded: false
    };
    
    // Add the new group
    setGroups([newGroup]);
    
    // Set as active group but DON'T clear selections (keep table state)
    setActiveGroupId(newGroupId);
    setGroupCounter(prev => prev + 1);
    
    console.log('[WaveExportV4] ✅ Created first group with preserved waves:', newGroup.name, initialWaves);
  };
  
  // Create a new group
  const createNewGroup = () => {
    if (!selectionManager) return;
    
    const newGroupId = `group_${Date.now()}`;
    
    console.log('[WaveExportV4] 🆕 Creating new group, current active group:', activeGroupId);
    
    // CRITICAL: Save current selections to the currently active group before creating new one
    const currentSelections = selectionManager.getSelectedWaves();
    console.log('[WaveExportV4] 💾 Saving current selections before creating new group:', currentSelections);
    
    // Update existing groups: save current selections and collapse all
    setGroups(prev => prev.map(g => {
      if (g.id === activeGroupId) {
        console.log('[WaveExportV4] 📝 Saving selections to group', g.id, ':', currentSelections);
        return { ...g, selectedWaves: [...currentSelections], isExpanded: false };
      } else {
        return { ...g, isExpanded: false };
      }
    }));
    
    // Create new empty group
    const newGroup: CustomGroup = {
      id: newGroupId,
      name: `Custom Group ${groupCounter}`,
      selectedWaves: [], // Always start with empty selections
      isExpanded: true,
      isLoaded: false
    };
    
    // Add the new group
    setGroups(prev => [...prev, newGroup]);
    
    // Switch to new group and clear table selections
    setActiveGroupId(newGroupId);
    setGroupCounter(prev => prev + 1);
    selectionManager.clearSelection();
    
    console.log('[WaveExportV4] ✅ Created new empty group:', newGroup.name);
  };
  
  // Switch to a different group
  const switchToGroup = (groupId: string) => {
    if (!selectionManager) return;
    
    const targetGroup = groups.find(g => g.id === groupId);
    if (!targetGroup) return;
    
    // Don't allow switching to exported/locked groups
    if (targetGroup.isLoaded) {
      alert('Cannot edit an exported group. The selections are locked in place.');
      return;
    }
    
    console.log('[WaveExportV4] 🔄 Switching from group', activeGroupId, 'to group', groupId);
    
    // CRITICAL: Save current selections to the currently active group first
    const currentSelections = selectionManager.getSelectedWaves();
    console.log('[WaveExportV4] 💾 Saving current selections:', currentSelections, 'to group', activeGroupId);
    
    // Update groups: save current selections and set expansion states
    setGroups(prev => prev.map(g => {
      if (g.id === activeGroupId) {
        // Save current selections to the previously active group
        console.log('[WaveExportV4] 📝 Updating group', g.id, 'with selections:', currentSelections);
        return { ...g, selectedWaves: [...currentSelections], isExpanded: false };
      } else if (g.id === groupId) {
        // Expand the target group
        return { ...g, isExpanded: true };
      } else {
        // Collapse all other groups
        return { ...g, isExpanded: false };
      }
    }));
    
    // Switch active group
    setActiveGroupId(groupId);
    
    // Clear current selections and restore the target group's selections
    selectionManager.clearSelection();
    
    console.log('[WaveExportV4] 🔄 Restoring selections for target group:', targetGroup.selectedWaves);
    targetGroup.selectedWaves.forEach(waveNumber => {
      selectionManager.toggleWave(waveNumber);
    });
  };
  
  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, isExpanded: !g.isExpanded } : g
    ));
  };
  
  // Delete a group
  const deleteGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    if (group.isLoaded) {
      alert('Cannot delete a group that has been exported to the analysis grid.');
      return;
    }
    
    // Remove group
    setGroups(prev => prev.filter(g => g.id !== groupId));
    
    // Clear active group if it was deleted
    if (activeGroupId === groupId) {
      setActiveGroupId(null);
      if (selectionManager) {
        selectionManager.clearSelection();
      }
    }
  };
  
  // Export group
  const exportGroup = async (group: CustomGroup) => {
    if (!onWaveExport || group.selectedWaves.length === 0) {
      alert('No waves selected for export. Please select waves first.');
      return;
    }
    
    if (group.isLoaded) {
      alert('This group has already been exported and is locked.');
      return;
    }
    
    console.log(`[WaveExportV4] 📤 Exporting ${group.name} with waves:`, group.selectedWaves);
    
    setIsExporting(true);
    try {
      // Export all selected waves (no duplicate filtering - keep it simple)
      await onWaveExport(group.selectedWaves, group.name);
      
      // Lock the group after successful export
      setGroups(prev => prev.map(g => 
        g.id === group.id ? { ...g, isLoaded: true } : g
      ));
      
      // If this was the active group, clear active status since it's now locked
      if (activeGroupId === group.id) {
        setActiveGroupId(null);
        if (selectionManager) {
          selectionManager.clearSelection();
        }
      }
      
      console.log(`[WaveExportV4] ✅ Successfully exported and locked ${group.name}`);
    } catch (error) {
      console.error('[WaveExportV4] ❌ Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Get champion image URL
  const getChampionImageUrl = (name: string) => 
    championsAssets[name]?.championImageUrl || '';
  
  // Render champion preview for a wave
  const renderChampionSide = (
    name: string,
    wave: WaveData | undefined,
    champData: ChampionBuildData | null | undefined,
    team: 'blue' | 'red'
  ) => {
    if (!wave) {
      return (
        <div className={`v4-wave-export-champ-col ${team} missing`}>
          <div className="v4-wave-export-champ-meta">
            <div className="v4-wave-export-champ-portrait">
              <img src={getChampionImageUrl(name)} alt={name} className="v4-wave-export-champ-portrait-image" />
            </div>
            <div className="v4-wave-export-meta-text">
              <div className="v4-wave-export-champ-name">{name}</div>
              <div className="v4-wave-export-meta-sub">No data</div>
            </div>
          </div>
        </div>
      );
    }

    // Items: 6 slots normally, 7 if boots slot item present (Bot lane quest)
    const allItems = [...wave.inventory];
    if (wave.bootsSlotItem) {
      allItems.push(wave.bootsSlotItem);
    }
    const slotCount = wave.bootsSlotItem ? 7 : 6;
    const items = allItems.slice(0, slotCount);
    const itemSlots = Array.from({ length: slotCount }, (_, i) => items[i] ?? null);

    return (
        <div className={`v4-wave-export-champ-col ${team}`}>
        {/* Meta info with items in same row */}
        <div className="v4-wave-export-champ-meta">
          <div className="v4-wave-export-champ-portrait">
            <img src={getChampionImageUrl(name)} alt={name} className="v4-wave-export-champ-portrait-image" />
          </div>
          <div className="v4-wave-export-meta-text">
            <div className="v4-wave-export-champ-name">{name}</div>
            <div className="v4-wave-export-meta-line">
              <span className="v4-wave-export-meta-level">Lv{wave.level}</span>
              <span className="v4-wave-export-meta-sep">•</span>
              <span className="v4-wave-export-meta-gold">{wave.goldRemaining}g</span>
            </div>
          </div>
          {/* Items in the same row */}
          <div className="v4-wave-export-items-row">
            {itemSlots.map((item, i) => (
              <div key={i} className={`v4-wave-export-item-slot ${item ? '' : 'empty'}`} title={item?.name || ''}>
                {item && <img src={getItemImageUrl(item.id)} alt={item.name} />}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`v4-wave-export-panel ${className}`}>
      <div className="v4-wave-export-content">
        
        {/* Custom Groups */}
        {groups.map(group => {
          const isActive = activeGroupId === group.id;
          const selectedCount = group.selectedWaves.length;
          
          return (
            <div 
              key={group.id} 
              className={`v4-wave-export-group-preview custom-group ${isActive ? 'active-group' : ''}`}
            >
              <div className="v4-wave-export-group-header">
                <button 
                  className="v4-wave-export-group-toggle" 
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="v4-export-arrow">
                    {group.isExpanded ? '▼' : '▶'}
                  </span>
                  <div className="v4-wave-export-group-title">
                    <h5 className={`v4-wave-export-group-name ${isActive ? 'active' : ''}`}>
                      {group.name}
                      {isActive && <span className="v4-active-indicator"> (Active)</span>}
                      {group.isLoaded && <span className="v4-loaded-indicator"> ✓ Loaded</span>}
                    </h5>
                    <span className="v4-wave-export-group-subtitle">
                      {selectedCount} wave{selectedCount === 1 ? '' : 's'}
                    </span>
                  </div>
                </button>
                
                <div className="v4-wave-export-group-actions">
                  {!isActive && !group.isLoaded && (
                    <button
                      type="button"
                      className="v4-wave-export-switch-group-btn"
                      onClick={() => switchToGroup(group.id)}
                      title="Switch to this group for wave selection"
                    >
                      Select
                    </button>
                  )}
                  <button
                    type="button"
                    className="v4-wave-export-export-group-btn"
                    onClick={() => exportGroup(group)}
                    disabled={selectedCount === 0 || isExporting || group.isLoaded}
                    title={group.isLoaded ? "Already exported" : "Export selected waves in this group"}
                  >
                    {group.isLoaded ? 'Exported' : (isExporting ? 'Exporting...' : 'Export')}
                  </button>
                </div>
              </div>

              {group.isExpanded && (
                <div className="v4-wave-export-group-content">
                  {group.selectedWaves.length === 0 ? (
                    <div className="v4-wave-export-empty-custom-group">
                      <p>No waves selected. Click waves in the table to add them to this group.</p>
                    </div>
                  ) : (
                    group.selectedWaves.map(waveNumber => {
                      const primaryWave = primaryWaves.find(w => w.waveNumber === waveNumber);
                      const opponentWave = opponentWaves?.find(w => w.waveNumber === waveNumber);
                      
                      if (!primaryWave) return null;
                      
                      const isAlreadyLoaded = loadedWaves.includes(waveNumber);
                      
                      return (
                        <div key={waveNumber} className={`v4-wave-export-wave-pair-preview-item ${isAlreadyLoaded ? 'already-loaded' : 'new-wave'}`}>
                          <div className="v4-wave-export-wave-pair-header">
                            <span className="v4-wave-export-wave-number">
                              Wave {waveNumber}
                              {isAlreadyLoaded && <span className="wave-already-loaded-indicator"> ✓ Loaded</span>}
                            </span>
                          </div>

                          {/* Champion preview row */}
                          <div className="v4-wave-export-wave-pair-row">
                            {renderChampionSide(primaryChampionName, primaryWave, primaryChampionData, 'blue')}

                            <div className="v4-wave-export-vs-col">
                              <span>VS</span>
                            </div>

                            {renderChampionSide(
                              opponentChampionName || 'Garen',
                              opponentWave ?? primaryWave,
                              opponentChampionData,
                              'red'
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Add Group Button */}
        <div className="v4-wave-export-add-group-section">
          <button
            type="button"
            className="v4-wave-export-add-group-btn"
            onClick={createNewGroup}
            title="Create a new custom group"
          >
            + Add Group
          </button>
        </div>

        {/* Error states */}
        {error && (
          <div className="v4-wave-export-error">
            <strong>Error:</strong> {error}
          </div>
        )}

      </div>
    </div>
  );
};

export default WaveExportV4;
